// Card #3098 (Scoreboard 2.0, docs/score/scoreboard-2.0.md "Mode types", spec
// types 1 and 2) -- the Class Summary mode type, ONE layout with two field
// sets picked by the mode's `fieldSet` (resolveFieldSet: an absent or
// unrecognised value reads as the default, detailed, never an error):
//   - detailed: class header + judges + sponsor line + the class START LIST
//     (time, CNO, rider, status/division, horse), matching
//     demo/scoreboard/2.0/type1-class-summary-detailed.html.
//   - simple: class header (+ test name) and a facts grid (judges, start /
//     estimated finish, total rides, ring, sponsor), matching
//     demo/scoreboard/2.0/type2-class-summary-simple.html. No start list.
// Every field is individually omittable: a key the payload does not carry
// renders no row and no empty label. Judge home city/state/country does not
// exist in cloud data, is never composed (score-api composeClassSummary), and
// is never rendered here.
//
// By division: the detailed start list reuses card #2332's rotation. This file
// owns NO timer and NO layout resolver of its own: DivisionSlideshow (one
// division on screen at a time, advancing on the operator's dwell) and
// groupRankedListByDivision are imported from RankedListLayout, and the
// by-division-or-combined choice reads the same resolveResultLayout the
// ranked list uses. startList rows carry entryIdentity.riderStatus, so the
// division grouping needs no new payload key.
//
// Column presence (Time, CNO, Rider, Status, Horse) is computed ONCE against
// the FULL start list (computeStartListColumns) and handed to every slide, so
// the headings never change as the rotation advances (#1780 AC5 / #1791).
//
// Standalone like MidRideSimpleLayout: it does not touch SingleEntryLayout or
// RankedListLayout's own markup. Nothing composable never reaches this
// component: classifyBoardRenderState routes it to the Blank fallback first.
import { formatFvdStampTime } from "common-fe/venueTime";
import { RESULT_LAYOUTS, resolveFieldSet, FIELD_SETS, resolveResultLayout, resolveResultDwellSeconds } from "score-common/constants";
import ClassHeader from "./ClassHeader";
import Banner from "./Banner";
import { computeIdentityColumns } from "./RankedList";
import { DivisionSlideshow, groupRankedListByDivision } from "./RankedListLayout";
import "./ClassSummaryLayout.css";

// A start list longer than one column holds at projector legibility splits into
// side-by-side columns of this many rows, read top-to-bottom then left-to-right
// (the same doctrine as the ranked list's groups of 10, #1791: wider, never
// truncated, never a second rotation).
const START_LIST_GROUP_SIZE = 10;

// Heading overrides: the mockup's heading for the status column reads
// "Status/Div"; every other heading is the shared identity column's own.
const HEADING_OVERRIDES = { riderStatus: "Status/Div" };

// Which columns the start list shows, from the FULL list. `showTime` follows the
// same #1780 AC5 rule as the identity sub-keys: a Time column exists only when
// at least one row has a ride time.
export function computeStartListColumns(startList) {
  const rows = Array.isArray(startList) ? startList : [];
  return {
    showTime: rows.some((row) => !!row?.rideTime),
    identityColumns: computeIdentityColumns(rows, { includeStatus: true }),
  };
}

function chunkRows(rows) {
  const chunks = [];
  for (let start = 0; start < rows.length; start += START_LIST_GROUP_SIZE) {
    chunks.push(rows.slice(start, start + START_LIST_GROUP_SIZE));
  }
  return chunks;
}

function StartListTable({ rows, columns }) {
  if (!rows.length) return null;
  return (
    <table className="start-list" data-testid="start-list">
      <thead>
        <tr data-testid="start-list-head-row">
          {columns.showTime ? (
            <th scope="col" className="start-list__time" data-testid="start-list-heading-time">
              Time
            </th>
          ) : null}
          {columns.identityColumns.map((column) => (
            <th
              key={column.key}
              scope="col"
              className={column.className}
              data-testid={`start-list-heading-${column.key}`}
            >
              {HEADING_OVERRIDES[column.key] || column.heading}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={index} data-testid="start-list-row">
            {columns.showTime ? (
              <td className="start-list__time">
                {row.rideTime ? <span data-testid="start-list-time">{formatFvdStampTime(row.rideTime)}</span> : null}
              </td>
            ) : null}
            {columns.identityColumns.map((column) => {
              const value = row?.entryIdentity?.[column.key];
              return (
                <td key={column.key} className={column.className}>
                  {column.present(value) ? <span data-testid={column.testid}>{column.render(value)}</span> : null}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function StartList({ fields }) {
  const startList = Array.isArray(fields?.startList) ? fields.startList : [];
  if (!startList.length) return null;

  // Computed ONCE against the full list, passed into every table on every
  // slide, so the headings cannot change as the slideshow advances.
  const columns = computeStartListColumns(startList);

  function renderScope(rows) {
    const chunks = chunkRows(rows);
    if (chunks.length > 1) {
      return (
        <div className="class-summary__columns" data-testid="class-summary-columns">
          {chunks.map((chunk, index) => (
            <StartListTable key={index} rows={chunk} columns={columns} />
          ))}
        </div>
      );
    }
    return <StartListTable rows={rows} columns={columns} />;
  }

  // Same gate as RankedListLayout: COMBINED (or a bad stored value resolving
  // to it) skips the grouping; a class where NO row carries a division renders
  // one table with no heading and no rotation.
  const resultLayout = resolveResultLayout(fields.resultLayout);
  const groups = groupRankedListByDivision(startList);
  const hasDivisions = resultLayout !== RESULT_LAYOUTS.COMBINED && groups.some((group) => group.key != null);

  return hasDivisions ? (
    <DivisionSlideshow
      groups={groups}
      dwellSeconds={resolveResultDwellSeconds(fields.resultDwellSeconds)}
      resetKey={String(fields.classNumberName?.classNumber ?? "")}
      renderScope={renderScope}
    />
  ) : (
    renderScope(startList)
  );
}

function SponsorLine({ sponsor }) {
  const text = sponsor?.textLine;
  if (!text) return null;
  return (
    <p className="class-summary__sponsor" data-testid="class-summary-sponsor">
      <span className="class-summary__sponsor-label">Sponsor</span>
      <span data-testid="class-summary-sponsor-text">{text}</span>
    </p>
  );
}

function FactRow({ label, testid, children }) {
  return (
    <div className="class-summary__fact" data-testid={testid}>
      <div className="class-summary__fact-label">{label}</div>
      <div className="class-summary__fact-value">{children}</div>
    </div>
  );
}

function ringLabel(ring) {
  const text = String(ring);
  return /^ring\b/i.test(text) ? text : `Ring ${text}`;
}

function FactsGrid({ fields, sponsor }) {
  const judges = Array.isArray(fields.judgesPositions) ? fields.judgesPositions : [];
  const start = fields.classStart ? formatFvdStampTime(fields.classStart) : null;
  const finish = fields.estimatedFinish ? formatFvdStampTime(fields.estimatedFinish) : null;
  return (
    <div className="class-summary__facts" data-testid="class-summary-facts">
      {judges.length > 0 ? (
        <FactRow label="Judges" testid="class-summary-fact-judges">
          {judges.map(({ position, judges: names }) => (
            <span key={position} className="class-summary__judge" data-testid="class-summary-judge">
              <span className="class-summary__judge-position">{position}</span> {(names || []).join(", ")}
            </span>
          ))}
        </FactRow>
      ) : null}
      {start || finish ? (
        <FactRow label="Start / Est. Finish" testid="class-summary-fact-times">
          {[start, finish].filter(Boolean).join(" — ")}
        </FactRow>
      ) : null}
      {fields.totalRides != null ? (
        <FactRow label="Total Rides" testid="class-summary-fact-total-rides">
          {fields.totalRides}
        </FactRow>
      ) : null}
      {fields.ring ? (
        <FactRow label="Ring" testid="class-summary-fact-ring">
          {ringLabel(fields.ring)}
        </FactRow>
      ) : null}
      {sponsor?.textLine ? (
        <FactRow label="Sponsor" testid="class-summary-fact-sponsor">
          {sponsor.textLine}
        </FactRow>
      ) : null}
    </div>
  );
}

export default function ClassSummaryLayout({ fields, banner, fieldSet, sponsor }) {
  if (!fields) return null;
  const isSimple = resolveFieldSet(fieldSet) === FIELD_SETS.SIMPLE;

  return (
    <div
      className="scoreboard-class-summary"
      data-testid="scoreboard-class-summary"
      data-field-set={isSimple ? FIELD_SETS.SIMPLE : FIELD_SETS.DETAILED}
    >
      <Banner banner={banner} />
      {isSimple ? (
        <>
          <ClassHeader classNumberName={fields.classNumberName} testName={fields.testName} />
          <FactsGrid fields={fields} sponsor={sponsor} />
        </>
      ) : (
        <>
          <ClassHeader classNumberName={fields.classNumberName} judgesPositions={fields.judgesPositions} />
          <SponsorLine sponsor={sponsor} />
          <div className="class-summary__body" data-testid="class-summary-body">
            <StartList fields={fields} />
          </div>
        </>
      )}
    </div>
  );
}
